import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { readStoredThemeKey, subscribeThemeKey } from './themeStore';
import type { ThemeKey } from './themeStore';

interface HeroUIProviderProps {
  children: ReactNode;
}

type ResolvedTheme = 'light' | 'dark';

function resolveTheme(themeKey: ThemeKey): ResolvedTheme {
  if (themeKey !== 'system') {
    return themeKey;
  }
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return 'light';
  }
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

export function HeroUIProvider({ children }: HeroUIProviderProps) {
  const [themeKey, setThemeKey] = useState<ThemeKey>(() => readStoredThemeKey());

  useEffect(() => {
    const unsubscribe = subscribeThemeKey(() => {
      setThemeKey(readStoredThemeKey());
    });
    return unsubscribe;
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    const resolved = resolveTheme(themeKey);
    root.classList.remove('theme-light', 'theme-dark');
    root.classList.add(resolved === 'dark' ? 'theme-dark' : 'theme-light');
    root.dataset.theme = resolved;
    root.style.colorScheme = resolved;
  }, [themeKey]);

  useEffect(() => {
    if (themeKey !== 'system' || typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return undefined;
    }
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const applySystemTheme = () => {
      const root = document.documentElement;
      const resolved = media.matches ? 'dark' : 'light';
      root.classList.remove('theme-light', 'theme-dark');
      root.classList.add(resolved === 'dark' ? 'theme-dark' : 'theme-light');
      root.dataset.theme = resolved;
      root.style.colorScheme = resolved;
    };
    applySystemTheme();

    if (media.addEventListener) {
      media.addEventListener('change', applySystemTheme);
      return () => media.removeEventListener('change', applySystemTheme);
    }

    media.addListener(applySystemTheme);
    return () => media.removeListener(applySystemTheme);
  }, [themeKey]);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return undefined;
    }
    const root = document.documentElement;
    const media = window.matchMedia('(prefers-reduced-transparency: reduce)');
    const updateTransparency = () => {
      root.classList.toggle('reduce-transparency', media.matches);
    };
    updateTransparency();

    if (media.addEventListener) {
      media.addEventListener('change', updateTransparency);
      return () => media.removeEventListener('change', updateTransparency);
    }

    media.addListener(updateTransparency);
    return () => media.removeListener(updateTransparency);
  }, []);

  return <>{children}</>;
}
